import { AlignCenterOutlined, AlignLeftOutlined } from '@ant-design/icons'
import { Editor } from '@monaco-editor/react'
import { Flex, Switch, theme } from 'antd'
import React, { useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { Toolbox } from '~/plugins/toolbox/shared'
import {
  applyLanguageAfter,
  applyLanguageBefore,
  LanguageKey,
} from './languages/language'

const EditorWrapper = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
`

const { useToken } = theme

type Props = {
  id?: string
  value?: string
  initialValues?: string
  onChange?: (value: string) => void
  height?: string
  language?: LanguageKey | string
  isWrapped?: boolean
  languageConfiguration?: any
  onCommandSave?: () => void
}

export const CodeInput: React.FC<Props> = ({
  id,
  value,
  initialValues,
  height = '50vh',
  onChange,
  language = 'javascript',
  isWrapped: isWrappedDefault = false,
  languageConfiguration,
  onCommandSave,
}) => {
  const [commandSaveDate, setCommandSaveDate] = useState<Date>()

  const { token } = useToken()

  const [isWrapped, setWrapped] = useState(isWrappedDefault)

  useEffect(() => {
    if (commandSaveDate) {
      onCommandSave()
    }
  }, [commandSaveDate])

  const handleChange = (value: string) => {
    try {
      onChange?.(value)
    } catch (err) {
      // ignore
    }
  }

  const toggleWrap = () => {
    setWrapped(value => !value)
  }

  const hasValue = Toolbox.Utility.isDefined(value)

  return (
    <>
      <div
        id={id}
        className="scrollbar--minimalist"
        style={{ minHeight: height, height: height }}
      >
        <Flex vertical style={{ minHeight: height, height: '100%' }}>
          <Flex justify="left" align="center" style={{ marginBottom: 8 }}>
            <Switch
              title="Toggle wrap"
              checked={isWrapped}
              onChange={toggleWrap}
              checkedChildren={<AlignLeftOutlined />}
              unCheckedChildren={<AlignCenterOutlined />}
            />
          </Flex>
          <Flex
            flex={1}
            vertical
            style={{
              border: `1px solid ${token.colorBorder}`,
              borderRadius: '5px',
              padding: '4px',
              background: language === 'typescript' ? 'black' : 'transparent',
              height: '100%',
            }}
          >
            <EditorWrapper>
              <Editor
                height="100%" // Ensure Monaco editor takes full height
                width="100%"
                defaultLanguage={language}
                theme={language}
                defaultValue={hasValue ? value : initialValues}
                onChange={handleChange}
                options={{
                  scrollBeyondLastLine: false,
                  wordWrap: isWrapped ? 'on' : 'off',
                  minimap: { enabled: false }, // Disable minimap
                  overviewRulerLanes: 0, // Disable the overview ruler
                }}
                beforeMount={monaco => {
                  applyLanguageBefore(monaco, language, languageConfiguration)
                }}
                onMount={(editor, monaco) => {
                  applyLanguageAfter(
                    editor,
                    monaco,
                    language,
                    languageConfiguration,
                  )

                  if (onCommandSave) {
                    editor.addCommand(
                      monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
                      () => {
                        setCommandSaveDate(new Date())
                      },
                    )
                  }

                  return () => {
                    editor.dispose() // Dispose of the editor instance
                    monaco.editor.getModels().forEach(model => model.dispose()) // Clear old models
                  }
                }}
              />
            </EditorWrapper>
          </Flex>
        </Flex>
      </div>
    </>
  )
}
